import React, { useEffect, useState } from 'react';
import { ArrowLeft, BrainCircuit, CheckCircle2, Layers3, LockKeyhole, RefreshCw, Route, UsersRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { legalOperationsService } from '../../../services/legalOperationsService';

const moduleLabels = {
  timeline: 'السجل الزمني',
  assignments: 'الإسناد',
  internal_notes: 'الملاحظات الداخلية',
  ai_analysis: 'تحليل AI',
  evidence: 'مركز الأدلة',
  communications: 'التواصل',
  tasks: 'المهام',
  hearings: 'الجلسات',
  documents: 'المستندات',
  drafts: 'المسودات',
  claim_estimation: 'تقدير المطالبات',
};

const dashboardLabels = {
  operations_control: 'مركز التحكم',
  legal_operations: 'عمليات القضايا',
  lawyer_workspace: 'مساحة المحامي',
  secretary_desk: 'مكتب المتابعة',
  audit_workspace: 'المراجعة والتدقيق',
  client_portal: 'بوابة العميل',
};

const LegalOperationsModelPage = () => {
  const navigate = useNavigate();
  const { token } = useAdminAuth();
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [seedMessage, setSeedMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setMetadata(await legalOperationsService.getMetadata(token));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const seedRoles = async () => {
    setSeedMessage('');
    try {
      const response = await legalOperationsService.seedRoles(token);
      setSeedMessage(`تمت تهيئة ${response.seeded.length} أدوار نظامية في قاعدة البيانات.`);
      await load();
    } catch (seedError) {
      setSeedMessage(seedError.message);
    }
  };

  return (
    <LaborOperationsLayout>
      <main className="mx-auto max-w-[1500px] px-6 pb-14 pt-10 sm:px-12" dir="rtl">
        <header className="lo-hero px-1 pb-9 pt-2">
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div>
              <button type="button" onClick={() => navigate('/admin/labor-intake')} className="mb-4 inline-flex items-center gap-2 text-xs font-bold text-[#988d7d]">
                <ArrowLeft className="h-4 w-4 rotate-180" /> العودة لملفات القضايا
              </button>
              <p className="lo-kicker font-bold">LEGAL CASE OPERATING SYSTEM</p>
              <h1 className="lo-heading legal-title mt-4 font-bold">نموذج التشغيل القانوني</h1>
              <p className="lo-lead mt-3">قراءة حية لهيكل المنصة والأدوار ومسار القضية وخط التحليل من خدمة التشغيل الجديدة.</p>
            </div>
            <div className="flex items-center gap-3">
              {metadata?.role === 'super_admin' && (
                <button type="button" onClick={seedRoles} className="rounded-xl bg-[#33455d] px-4 py-3 text-xs font-bold text-white">
                  تهيئة الأدوار النظامية
                </button>
              )}
              <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-xl bg-[#ece9e2] px-5 py-3 text-[14px] font-bold text-[#293a50]">
                <RefreshCw className="h-4 w-4" /> تحديث الاتصال
              </button>
            </div>
          </div>
          {seedMessage && <p className="mt-5 rounded-xl bg-[#f5eddf] px-4 py-3 text-sm font-bold text-[#806238]">{seedMessage}</p>}
        </header>

        {loading && <div className="lo-surface mt-6 rounded-[18px] p-10 text-center text-[15px] text-[#697483]">جاري قراءة نموذج التشغيل من الخادم...</div>}
        {error && <div className="mt-5 rounded-[20px] border border-[#ecdacb] bg-[#faf1e8] p-5 text-sm font-bold text-[#99623b]">{error}</div>}

        {metadata && (
          <>
            <section className="mt-5 grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
              <article className="lo-surface rounded-[18px] p-7">
                <div className="mb-5 flex items-center gap-3">
                  <Layers3 className="h-5 w-5 text-[#948066]" />
                  <h2 className="font-sans text-lg font-bold text-[#273950]">طبقات المنصة</h2>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {metadata.platformLayers.map((layer) => (
                    <div key={layer.key} className="border-b border-[#ece8e0] p-4">
                      <p className="text-[16px] font-bold text-[#293a50]">{layer.label}</p>
                      <p className="mt-2 text-[14px] leading-7 text-[#697483]">{layer.purpose}</p>
                    </div>
                  ))}
                </div>
              </article>

              <article className="lo-surface rounded-[18px] p-7">
                <div className="mb-5 flex items-center gap-3">
                  <LockKeyhole className="h-5 w-5 text-[#948066]" />
                  <h2 className="font-sans text-lg font-bold text-[#273950]">وصولك الحالي</h2>
                </div>
                <p className="rounded-xl bg-[#f5eddf] px-4 py-3 text-sm font-bold text-[#273950]">
                  {dashboardLabels[metadata.workspace.dashboard] || metadata.workspace.dashboard}
                </p>
                <p className="mt-5 text-xs font-bold text-[#988d7d]">الصلاحيات التشغيلية المحملة من API</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {metadata.permissions.map((permission) => (
                    <span key={permission} className="rounded-lg bg-[#f0eee8] px-3 py-1.5 text-[13px] font-medium text-[#5f6a78]">{permission}</span>
                  ))}
                </div>
              </article>
            </section>

            <section className="lo-surface mt-10 rounded-[18px] p-7">
              <div className="mb-5 flex items-center gap-3">
                <Route className="h-5 w-5 text-[#948066]" />
                <h2 className="font-sans text-lg font-bold text-[#273950]">Workflow دورة القضية</h2>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {metadata.workflow.map((stage, index) => (
                  <div key={stage.key} className="min-w-[164px] border-l border-[#ece8e0] p-5">
                    <p className="text-[13px] font-bold text-[#948066]">{String(index + 1).padStart(2, '0')}</p>
                    <p className="mt-3 text-[16px] font-bold text-[#293a50]">{stage.label}</p>
                    <p className="mt-2 text-[13px] text-[#697483]">{stage.key}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-5 grid gap-4 xl:grid-cols-[1fr_1fr]">
              <article className="lo-surface rounded-[18px] p-7">
                <div className="mb-5 flex items-center gap-3">
                  <UsersRound className="h-5 w-5 text-[#948066]" />
                  <h2 className="font-sans text-lg font-bold text-[#273950]">الأدوار وبيئات العمل</h2>
                </div>
                <div className="space-y-2">
                  {metadata.roles.map((role) => (
                    <div key={role.id} className="flex items-center justify-between border-b border-[#ece8e0] px-2 py-4">
                      <div>
                        <p className="text-sm font-bold text-[#273950]">{role.name}</p>
                        <p className="mt-1 text-[13px] text-[#697483]">{dashboardLabels[role.workspace] || role.workspace}</p>
                      </div>
                      <span className="rounded-lg bg-[#eee8dd] px-3 py-1.5 text-[13px] font-bold text-[#806f58]">
                        {role.permissionsCount === 1 ? 'كامل' : `${role.permissionsCount} صلاحيات`}
                      </span>
                    </div>
                  ))}
                </div>
              </article>

              <article className="lo-surface rounded-[18px] p-7">
                <div className="mb-5 flex items-center gap-3">
                  <BrainCircuit className="h-5 w-5 text-[#948066]" />
                  <h2 className="font-sans text-lg font-bold text-[#273950]">AI Legal Pipeline</h2>
                </div>
                <div className="space-y-2">
                  {metadata.aiPipeline.map((stage) => (
                    <div key={stage.key} className="flex items-center gap-3 border-b border-[#ece8e0] px-2 py-4">
                      <CheckCircle2 className="h-4 w-4 text-[#948066]" />
                      <div>
                        <p className="text-sm font-bold text-[#273950]">{stage.label}</p>
                        <p className="mt-1 text-[13px] text-[#697483]">{stage.key}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 rounded-xl border border-[#ecdacb] bg-[#faf1e8] px-4 py-3 text-xs leading-6 text-[#806238]">{metadata.analysisDisclaimer}</p>
              </article>
            </section>

            <section className="lo-surface mt-10 rounded-[18px] p-7">
              <h2 className="font-sans text-lg font-bold text-[#273950]">مكونات ملف القضية</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {metadata.caseModules.map((module) => (
                  <span key={module} className="rounded-xl bg-[#f0eee8] px-4 py-3 text-[14px] font-bold text-[#293a50]">
                    {moduleLabels[module] || module}
                  </span>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </LaborOperationsLayout>
  );
};

export default LegalOperationsModelPage;
